<template>
  <div v-if="artwork.id" class="artwork-info-side">
    <tk-ask-us-dialog
      v-model="showAskUsDialog"
      :topic="askUsTopic"
      :artwork_id="artwork.id"
      :country_id="askUsCountryId"
    />
    <tk-shipping-dialog
      v-model="showShippingDialog"
      :artworkId="artwork.id"
      @onAddToCart="
        showShippingDialog = false;
        addTocart();
      "
      @onOpenAskUs="openAskUs"
    />

    <payment-calculator-modal
      v-model="showPaymentCalculatorModal"
      :price="artwork.price"
    />

    <base-card class="px-4 pb-3" color="accent" height="100%">
      <!-- Artwork title -->
      <h2 class="pt-3 pb-2 mb-3 font-weight-bold">{{ artwork.title }}</h2>

      <!-- Artist title  -->
      <template v-if="artwork.artist">
        <router-link
          class="artist-content"
          v-if="artwork.artist.isVisible"
          :to="{
            name: 'artist.single',
            params: { artistSlug: artwork.artist.slug },
          }"
        >
          <v-layout pl-3 row align-center>
            <v-flex shrink pa-0 v-if="artwork.artist.media">
              <tk-media
                :media="artwork.artist.media"
                height="60"
                width="60"
                sizes="60px"
                :disableIntersection="true"
              />
            </v-flex>
            <v-flex>
              <div class="artwork-subtitle">{{ artwork.artist.name }}</div>
              <span class="artworks-link">{{
                $t("ARTWORK.SEE_ALL_ARTWORKS")
              }}</span>
            </v-flex>
          </v-layout>
        </router-link>

        <!-- If artist is not visible just show the name -->
        <h3 v-else class="artwork-subtitle">{{ artwork.artist.name }}</h3>
      </template>

      <!-- Gallery title -->
      <router-link
        class="artwork-subtitle pt-2 d-block"
        v-if="artwork.virtual_exhibition"
        :to="{
          name: 'galleries.single',
          params: { gallerySlug: artwork.virtual_exhibition.slug },
        }"
        >{{ artwork.virtual_exhibition.name || "In Gallery" }}</router-link
      >

      <v-layout mt-2 row align-center>
        <v-flex shrink pr-0>
          <v-icon  size="16" color="black">far fa-bell</v-icon>
        </v-flex>
        <v-flex py-0>
          <ArtworkSubscribeForm
            v-if="artwork.artist"
            :artist="artwork.artist"
            @update:subscription="artwork.artist.my_subscription_id = $event.id"
          />
        </v-flex>
      </v-layout>

      <h4 class="mt-2 subheading">
        {{ artwork.createdYear }} | {{ artwork.category.name }} |
        {{ artwork.technique.name }}
      </h4>
      <h4 class="subheading my-1 font-weight-medium">
        {{ $t("ARTWORK.SIZE") }}: {{ artwork.dimension }}
      </h4>

      <div class="addition-detail mt-3" v-if="!isCategorySculpture">
        <v-tooltip top v-if="artwork.isFramed">
          <template v-slot:activator="{ on }">
            <v-img
              v-on="on"
              contain
              height="30"
              width="30"
              src="/static/img/frame.png"
            />
          </template>
          <span>{{ $t("TOOLTIP.ARTWORK.FRAMED") }}</span>
        </v-tooltip>
        <v-tooltip top v-else>
          <template v-slot:activator="{ on }">
            <v-img
              v-on="on"
              contain
              height="30"
              width="30"
              src="/static/img/noframe.png"
            />
          </template>
          <span>{{ $t("TOOLTIP.ARTWORK.NO_FRAME") }}</span>
        </v-tooltip>
        <v-tooltip top v-if="artwork.isHangReady">
          <template v-slot:activator="{ on }">
            <v-img
              v-on="on"
              contain
              height="30"
              width="30"
              src="/static/img/hanging.png"
            />
          </template>
          <span>{{ $t("TOOLTIP.ARTWORK.HANG") }}</span>
        </v-tooltip>
        <v-tooltip top v-else>
          <template v-slot:activator="{ on }">
            <v-img
              v-on="on"
              contain
              height="30"
              width="30"
              src="/static/img/nohanging.png"
            />
          </template>
          <span>{{ $t("TOOLTIP.ARTWORK.NO_HANG") }}</span>
        </v-tooltip>
      </div>

      <template v-if="artwork.description">
        <p class="mt-4 mb-1 pre-wrap">{{ descriptionProxy }}</p>
        <div class="text-xs-right" v-if="artwork.description.length > 300">
          <tk-btn
            class="more-btn"
            small
            outline
            @click="descriptionExpanded = !descriptionExpanded"
            >{{
              descriptionExpanded ? $t("COMMON.LESS") : $t("COMMON.MORE")
            }}</tk-btn
          >
        </div>
      </template>

      <div
        v-if="artwork.artist.absenceUntil"
        class="mt-3"
        v-html="
          $t('ARTWORK.ABSENT_NOTE', { date: artwork.artist.absenceUntil })
        "
      ></div>

      <!-- Price area -->
      <div class="mt-5">
        <!-- Price -->
        <h3 class="display-1 font-weight-bold price-text">
          {{ artwork.price }} €
        </h3>
        <!-- Price calculator btn -->
        <tk-btn
          v-if="$i18n.locale === 'fi'"
          @click="showPaymentCalculatorModal = true"
          :disabled="!artwork.isForSale"
          flat
          class="clear-btn text-none calculator-btn my-2"
          v-text="'Osamaksulaskuri'"
        />
      </div>
      <v-layout row align-center justify-center>
        <v-flex pl-4>
          <v-layout row align-center>
            <v-flex shrink pa>
              <v-icon color="black">fas fa-truck</v-icon>
            </v-flex>
            <v-flex py-0>
              <tk-btn
                v-if="$i18n.locale === 'en'"
                flat
                tag="a"
                @click="showShippingDialog = true"
                :disabled="!artwork.isForSale || !artwork.isOrderable"
                class="clear-btn text-none caption"
              >
                <span class="text-xs-left">{{
                  $t("ARTWORK.DELIVERY_CALC_BTN")
                }}</span>
              </tk-btn>
              <span
                v-else
                class="text-xs-left text-none caption"
                style="color: black"
                v-html="$t('ARTWORK.DELIVERY_CALC_BTN')"
              ></span>
            </v-flex>
          </v-layout>

          <div v-if="$i18n.locale !== 'en'">
            <tk-btn
              flat
              tag="a"
              @click="showShippingDialog = true"
              :disabled="!artwork.isForSale || !artwork.isOrderable"
              class="clear-btn text-none caption"
            >
              <span class="text-xs-left">{{
                $t("ARTWORK.DELIVERY_CHARGES_BTN")
              }}</span>
            </tk-btn>
          </div>
        </v-flex>

        <v-flex text-xs-right shrink>
          <ReturnPolicyBtn />
        </v-flex>
      </v-layout>

      <!-- Buttons row -->
      <div class="mt-3">
        <!-- Add/Sold etc butons -->
        <template v-if="artwork.isForSale && !artwork.is_suspended">
          <tk-btn
            v-if="artwork.isOrderable"
            block
            :large="$vuetify.breakpoint.smAndUp"
            :disabled="isAlreadyAdded"
            color="primary"
            @click="addTocart"
            v-text="
              isAlreadyAdded
                ? $t('ARTWORK.ADDED_TO_CART')
                : $t('ARTWORK.ADD_TO_CART')
            "
          />
          <tk-btn
            v-else
            block
            :large="$vuetify.breakpoint.smAndUp"
            color="primary"
            @click="
              askUsTopic = 'shipping';
              showAskUsDialog = true;
            "
            v-text="$t('ARTWORK.ASK_ABOUT_SHIPPING')"
          />
        </template>
        <template v-else-if="artwork.is_suspended && artwork.virtual_exhibition">
          <tk-btn
            block
            :large="$vuetify.breakpoint.smAndUp"
            color="primary"
            :to="{
              name: 'galleries.single',
              params: { gallerySlug: artwork.virtual_exhibition.slug },
            }"
            v-t="'COMMON.IN_GALLERY'"
          />
        </template>

        <!-- SOLD button -->
        <template v-else>
          <tk-btn
            block
            :large="$vuetify.breakpoint.smAndUp"
            disabled
            color="primary"
            v-text="$t('COMMON.SOLD')"
          />
        </template>

        <!-- Favorites button -->
        <v-tooltip top max-width="200">
          <template v-slot:activator="{ on }">
            <tk-btn
              block
              outline
              v-on="isLoggedIn ? null : on"
              :loading="favoritePending"
              :large="$vuetify.breakpoint.smAndUp"
              @click="toggleFavorite"
            >
              <template v-if="isFavorite">
                <v-icon size="1.3rem">fas fa-star</v-icon>
                <span class="pl-2">{{ $t("ARTWORK.REMOVE_FROM_FAV") }}</span>
              </template>
              <template v-else>
                <v-icon size="1.3rem">far fa-star</v-icon>
                <span class="pl-2">{{ $t("ARTWORK.ADD_TO_FAV") }}</span>
              </template>
            </tk-btn>
          </template>
          <span>{{ $t("TOOLTIP.FAV_BTN") }}</span>
        </v-tooltip>
      </div>

      <!-- Views and favorites-->
      <v-layout row class="mb-3" align-center justify-start>
        <v-flex shrink v-if="artwork.views">
          <v-layout row align-center>
            <v-flex shrink pr-0>
              <v-icon size="30" color="black">far fa-eye</v-icon>
            </v-flex>
            <v-flex shrink>
              <h3>{{ artwork.views }}</h3>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex shrink v-if="artwork.favorites">
          <v-layout row align-center>
            <v-flex shrink pr-0>
              <v-icon size="25" color="black">far fa-star</v-icon>
            </v-flex>
            <v-flex shrink>
              <h3>{{ artwork.favorites }}</h3>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <!-- Actions butons -->
      <div class="action-btns mb-2 mt-1 ml-1">
        <v-list dense>
          <v-list-tile
            @click="
              askUsTopic = 'taiko';
              showAskUsDialog = true;
            "
          >
            <v-list-tile-title class="text-uppercase">{{
              $t("ARTWORK.ASK_US")
            }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile @click="showPageDialog('payment')">
            <v-list-tile-title class="text-uppercase">{{
              $t("ARTWORK.PAYMENT")
            }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile @click="showPageDialog('delivery')">
            <v-list-tile-title class="text-uppercase">{{
              $t("ARTWORK.DELIVERY")
            }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile href="https://www.taiko.art/returns">
            <v-list-tile-title class="text-uppercase">{{
              $t("ARTWORK.RETURNS")
            }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile @click="showPageDialog('abc')">
            <v-list-tile-title class="text-uppercase">{{
              $t("MENU.BUYERS_GUIDE")
            }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile :to="'/commission'">
            <v-list-tile-title class="text-uppercase">{{
              $t("Commission work")
            }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </div>

      <div class="text-xs-center py-3">
        <tk-btn
          aria-label="Share on facebook"
          flat
          class="social-icon"
          @click="share.facebook()"
        >
          <v-icon>fab fa-facebook-f</v-icon>
        </tk-btn>
        <tk-btn
          aria-label="Share on twitter"
          flat
          class="social-icon"
          @click="share.twitter()"
        >
          <v-icon>fab fa-twitter</v-icon>
        </tk-btn>
        <tk-btn
          aria-label="Share on linkedin"
          flat
          class="social-icon"
          @click="share.linkedin()"
        >
          <v-icon>fab fa-linkedin-in</v-icon>
        </tk-btn>
      </div>
    </base-card>

    <!-- Show payment options cards -->
    <tk-payment-options class="mt-4" />
  </div>
</template>

<script>
import * as SocialShare from "@/utils/SocialShare";
import { ErrorHandler } from "@/utils/ErrorHandler";
import ArtworkSubscribeForm from '../../../components/Domain/Artwork/Subscription/ArtworkSubscribeForm'

const handler = new ErrorHandler();

export default {
  props: {
    artwork: { required: true },
  },

  components: {
    ArtworkSubscribeForm,
    TkAskUsDialog: () => import("@/components/core/TkAskUsDialog"),
    TkShippingDialog: () => import("@/components/core/TkShippingDialog"),
    TkPaymentOptions: () => import("@/components/core/TkPaymentOptions"),
    ReturnPolicyBtn: () => import("./ReturnPolicyBtn"),
    PaymentCalculatorModal: () => import("./PaymentCalculatorModal"),
  },

  data() {
    return {
      descriptionExpanded: false,
      showAskUsDialog: false,
      showShippingDialog: false,
      askUsTopic: "taiko",
      share: SocialShare,
      askUsCountryId: null,
      favoritePending: false,
      showPaymentCalculatorModal: false,
    };
  },

  async mounted() {
    //    maksutuvaGetPlans(this.$i18n.locale)
  },

  computed: {
    descriptionProxy() {
      return !this.descriptionExpanded && this.artwork.description.length > 299
        ? this.artwork.description.substring(0, 300) + "..."
        : this.artwork.description;
    },
    isAlreadyAdded() {
      return this.$cart.isAlreadyAdded(this.artwork.id);
    },
    isCategorySculpture() {
      if (this.artwork.category && this.artwork.category.slug === "sculpture") {
        return true;
      }
      return false;
    },
    isFavorite() {
      return this.artwork.favoriteId ? true : false;
    },
    isLoggedIn() {
      return this.$auth && this.$auth.currentUser;
    },
  },

  methods: {
    showPageDialog(slug) {
      this.$app.pageDialog = {
        show: true,
        slug: slug,
      };
    },

    addTocart() {
      this.$cart.addItem(this.artwork);
      this.$router.push({ name: "cart" });
    },

    openAskUs(e) {
      this.askUsCountryId = e.id;
      this.showShippingDialog = false;
      this.askUsTopic = "shipping";
      this.showAskUsDialog = true;
    },

    async toggleFavorite() {
      if (!this.isLoggedIn) {
        this.$app.loginTitle = this.$t('ARTWORK.INFO.LOGIN_TITLE')
        this.$app.loginDialog = true;
        return;
      }
      this.favoritePending = true;

      await handler.handle(async () => {
        if (!this.artwork.favoriteId) {
          const res = await this.$auth.addToFavorite(
            "artwork",
            this.artwork.id
          );
          this.artwork.favoriteId = res.data["data"].id;
          this.artwork.favorites++;
        } else {
          await this.$auth.removeFavorite(this.artwork.favoriteId);
          this.artwork.favoriteId = null;
          this.artwork.favorites--;
        }
      });
      this.favoritePending = false;
    },
  },
};
</script>

<style lang="scss">
.artwork-info-side {
  .addition-detail {
    .v-image {
      display: inline-block;
      margin-right: 16px;
    }
  }

  .fav-btn {
    min-width: 30px;
    max-width: 30px;
  }

  .abc-icon {
    font-weight: 900;
    font-size: 1.3rem;
    margin-right: 8px;
  }

  .calculator-btn {
    font-weight: 800;
    margin-top: 8px;
    height: 24px;
    display: block;
    text-decoration: underline;
    font-weight: 600;
    &:hover {
      text-decoration: none;
    }
  }

  .artwork-subtitle {
    font-size: 1.2rem;
    font-weight: 500;
  }

  .price-text {
    @media only screen and (max-width: 400px) {
      font-size: 2rem !important;
    }
  }

  .artist-content {
    text-decoration: none;
    .artworks-link {
      font-weight: 500;
      text-decoration: underline;
    }

    &:hover {
      .artworks-link {
        text-decoration: none;
      }
    }
  }

  a {
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
}
</style>
